<script lang="ts" setup>
import type { CollectionFieldPath, Filter } from '#layers/data-model/public';
import type { FilterAction } from './utils/filter-action';
import type { FilterSchemaCollection } from './utils/filter-schema';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { CollectionFieldPathSelect } from '#layers/data-model/public';
import { useFilterActions } from '#layers/filters/composables/use-filter-actions';
import { useFilterGate } from '#layers/filters/composables/use-filter-gate';
import { createGroup } from '#layers/filters/composables/use-logical-filter';
import { useRelatedItemFilter } from '#layers/filters/composables/use-related-item-filter';
import { useRelatedItemGate } from '#layers/filters/composables/use-related-item-gate';
import { isPrimitiveFieldPath } from './utils/filter-path';
import { injectFilterSchemaContext } from './utils/filter-schema-context';

const {
	includeRelationFields = true,
	includeGroupAction = true,
	collection,
} = defineProps<{
	collection: FilterSchemaCollection;
	includeRelationFields?: boolean;
	includeGroupAction?: boolean;
	disabled?: boolean;
}>();

const emit = defineEmits<{
	addGroup: [];
	addFilter: [action: FilterAction];
	addCondition: [condition: Filter];
}>();

const { t } = useI18n();
const { findFieldById, findOppositeCollectionByRelationFieldId } = injectFilterSchemaContext();
const { buildRelatedItemCondition } = useRelatedItemFilter();
const includeSelectableRelationFields = computed(() => includeRelationFields !== false);
const gate = useFilterGate();
const { allowsFilterPath: allowsRelatedPath } = useRelatedItemGate();
const actions = useFilterActions();

function allowsFilterPath(path: CollectionFieldPath) {
	return isPrimitiveFieldPath(path, { findFieldById }) ? actions.resolve(path) !== undefined : allowsRelatedPath(path);
}

const ADD_GROUP_ACTION_ID = 'add-group';

const leadingActions = computed(() => includeGroupAction && gate.allowsClause(createGroup())
	? [{
			id: ADD_GROUP_ACTION_ID,
			label: t('filters.filter_group'),
			icon: 'lucide:plus',
		}]
	: [],
);

const availableFilters = computed(() => [
	...collection.primitiveFields,
	...(collection.relationFields ?? []).filter((field) => findOppositeCollectionByRelationFieldId(field.id)),
].filter((field) => allowsFilterPath([field.id])));

// A primitive selection adds a plain field filter; selecting a relation itself opens the item
// picker and adds a related-item match for that relation path.
async function onFieldSelect(fieldPath: CollectionFieldPath) {
	if (isPrimitiveFieldPath(fieldPath, { findFieldById })) {
		const action = actions.resolve(fieldPath);
		if (action) emit('addFilter', action);
		return;
	}

	const condition = await buildRelatedItemCondition(fieldPath);

	if (condition) emit('addCondition', condition);
}

function onAction(actionId: string) {
	if (includeGroupAction && actionId === ADD_GROUP_ACTION_ID) {
		emit('addGroup');
	}
}
</script>

<template>
	<CollectionFieldPathSelect
		:collection
		include-constrained-fields
		:include-relation-fields="includeSelectableRelationFields"
		:include-list-relation-fields="includeSelectableRelationFields"
		terminal="primitive-or-relation"
		size="md"
		:resolve-relation-collection="findOppositeCollectionByRelationFieldId"
		:filter-field-path="allowsFilterPath"
		:leading-actions
		:search-placeholder="$t('filters.filter_search_placeholder')"
		@action="onAction"
		@select="onFieldSelect"
	>
		<template #default="{ open }">
			<slot
				:available-filters
				:open
			>
				<DButton
					color="neutral"
					variant="ghost"
					icon="lucide:list-filter"
					:aria-label="$t('filters.filter')"
					:label="$t('filters.add_filter')"
					size="sm"
					:disabled
				/>
			</slot>
		</template>
	</CollectionFieldPathSelect>
</template>
